import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/DevicesFranquias_SalvarInput.ts';
import '../contracts/DevicesFranquias_ToggleActiveInput.ts';
import '../contracts/DevicesFranquias_SetupDeviceInput.ts';
import '../contracts/DevicesFranquias_FetchDeviceTokenInput.ts';
import '../contracts/DevicesFranquias_ReconnectDeviceInput.ts';
import '../contracts/DevicesFranquias_ToggleWebsocketInput.ts';
import '../contracts/DevicesFranquias_DisconnectDeviceInput.ts';

import '../contracts/DevicesFranquias_BuscaDeviceResponse';
import '../contracts/DevicesFranquias_DisconnectDeviceResponse';
import '../contracts/DevicesFranquias_FetchDeviceTokenResponse';
import '../contracts/DevicesFranquias_ReconnectDeviceResponse';
import '../contracts/DevicesFranquias_RemoverDeviceResponse';
import '../contracts/DevicesFranquias_SalvarResponse';
import '../contracts/DevicesFranquias_SetupDeviceResponse';
import '../contracts/DevicesFranquias_ToggleActiveResponse';
import '../contracts/DevicesFranquias_ToggleWebsocketResponse';
import type { DevicesFranquias_SalvarInput } from '../contracts/DevicesFranquias_SalvarInput';
import type { DevicesFranquias_SetupDeviceInput } from '../contracts/DevicesFranquias_SetupDeviceInput';
import type { DevicesFranquias_ToggleActiveInput } from '../contracts/DevicesFranquias_ToggleActiveInput';
import type { DevicesFranquias_FetchDeviceTokenInput } from '../contracts/DevicesFranquias_FetchDeviceTokenInput';
import type { DevicesFranquias_ReconnectDeviceInput } from '../contracts/DevicesFranquias_ReconnectDeviceInput';
import type { DevicesFranquias_ToggleWebsocketInput } from '../contracts/DevicesFranquias_ToggleWebsocketInput';
import type { DevicesFranquias_DisconnectDeviceInput } from '../contracts/DevicesFranquias_DisconnectDeviceInput';
/**
 * Composable for Devices Franquias operations
 * Provides methods to interact with the Devices Franquias API
 */
export function useDevicesFranquias() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch single Device by ID
   */
  const fetchDevice = async (deviceId: number) => {
    log('useDevicesFranquias', 'fetchDevice', 'Fetching Device', deviceId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/devices_franquias/buscaDevice/${deviceId}`, { responseContract: 'DevicesFranquias_BuscaDeviceResponse' });
      log('useDevicesFranquias', 'fetchDevice', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar dispositivo');
      }
    } catch (err) {
      log('useDevicesFranquias', 'fetchDevice', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save Device (create or update)
   */
  const saveDevice = async (deviceData: DevicesFranquias_SalvarInput) => {
    log('useDevicesFranquias', 'saveDevice', 'Saving Device', deviceData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('DevicesFranquias_SalvarInput', deviceData);
      const response = await httpService.post('/index.php/config/devices_franquias/salvar', deviceData, {
        responseContract: 'DevicesFranquias_SalvarResponse',
        inputContract: 'DevicesFranquias_SalvarInput'
      });
      log('useDevicesFranquias', 'saveDevice', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao salvar dispositivo');
      }
    } catch (err) {
      log('useDevicesFranquias', 'saveDevice', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Remove Device
   */
  const removerDevice = async (deviceId: number) => {
    log('useDevicesFranquias', 'removerDevice', 'Removing Device', deviceId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/devices_franquias/remover_device/${deviceId}`, { responseContract: 'DevicesFranquias_RemoverDeviceResponse' });
      log('useDevicesFranquias', 'removerDevice', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao remover dispositivo');
      }
    } catch (err) {
      log('useDevicesFranquias', 'removerDevice', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Setup WhatsApp device (Evolution API specific)
   */
  const setupDevice = async (deviceData: DevicesFranquias_SetupDeviceInput) => {
    log('useDevicesFranquias', 'setupDevice', 'Setting up device', deviceData);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/devices_franquias/setup_device', deviceData, { responseContract: 'DevicesFranquias_SetupDeviceResponse', inputContract: 'DevicesFranquias_SetupDeviceInput' });
      log('useDevicesFranquias', 'setupDevice', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao configurar dispositivo');
      }
    } catch (err) {
      log('useDevicesFranquias', 'setupDevice', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch device token from Evolution API and save to database
   */
  const fetchDeviceToken = async (deviceId: number, instanceName: string) => {
    log('useDevicesFranquias', 'fetchDeviceToken', 'Fetching device token', { deviceId, instanceName });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: DevicesFranquias_FetchDeviceTokenInput = {
        device_id: deviceId,
        instance_name: instanceName
      };
      const response = await httpService.post('/index.php/config/devices_franquias/fetch_device_token',
        payload,
        { responseContract: 'DevicesFranquias_FetchDeviceTokenResponse', inputContract: 'DevicesFranquias_FetchDeviceTokenInput' });
      log('useDevicesFranquias', 'fetchDeviceToken', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao buscar device token');
      }
    } catch (err) {
      log('useDevicesFranquias', 'fetchDeviceToken', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Toggle active status of a Device
   */
  const toggleActive = async (deviceId: number, active: boolean) => {
    log('useDevicesFranquias', 'toggleActive', 'Toggling active', { deviceId, active });

    try {
      const payload: DevicesFranquias_ToggleActiveInput = {
        device_id: deviceId,
        active
      };
      validate('DevicesFranquias_ToggleActiveInput', payload);
      const response = await httpService.post('/index.php/config/devices_franquias/toggle_active', payload, {
        responseContract: 'DevicesFranquias_ToggleActiveResponse',
        inputContract: 'DevicesFranquias_ToggleActiveInput'
      });

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao atualizar status');
      }
    } catch (err) {
      log('useDevicesFranquias', 'toggleActive', 'Error', err);
      throw err;
    }
  };

  /**
   * Reconnect a WhatsApp device (re-trigger Evolution API connection)
   */
  const reconnectDevice = async (instanceName: string) => {
    log('useDevicesFranquias', 'reconnectDevice', 'Reconnecting device', instanceName);

    try {
      const payload: DevicesFranquias_ReconnectDeviceInput = { instance_name: instanceName };
      const response = await httpService.post('/index.php/config/devices_franquias/reconnect_device',
        payload,
        { responseContract: 'DevicesFranquias_ReconnectDeviceResponse', inputContract: 'DevicesFranquias_ReconnectDeviceInput' });

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao reconectar dispositivo');
      }
    } catch (err) {
      log('useDevicesFranquias', 'reconnectDevice', 'Error', err);
      throw err;
    }
  };

  /**
   * Toggle WebSocket for a WhatsApp device (Evolution API specific)
   */
  const toggleWebsocket = async (instanceName: string, enabled: boolean) => {
    log('useDevicesFranquias', 'toggleWebsocket', 'Toggling websocket', { instanceName, enabled });

    try {
      const payload: DevicesFranquias_ToggleWebsocketInput = { instance_name: instanceName, enabled };
      const response = await httpService.post('/index.php/config/devices_franquias/toggle_websocket',
        payload,
        { responseContract: 'DevicesFranquias_ToggleWebsocketResponse', inputContract: 'DevicesFranquias_ToggleWebsocketInput' });

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao configurar WebSocket');
      }
    } catch (err) {
      log('useDevicesFranquias', 'toggleWebsocket', 'Error', err);
      throw err;
    }
  };

  /**
   * Disconnect WhatsApp device (Evolution API specific)
   */
  const disconnectDevice = async (instanceName: string) => {
    log('useDevicesFranquias', 'disconnectDevice', 'Disconnecting device', instanceName);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: DevicesFranquias_DisconnectDeviceInput = { instance_name: instanceName };
      const response = await httpService.post('/index.php/config/devices_franquias/disconnect_device',
        payload,
        { responseContract: 'DevicesFranquias_DisconnectDeviceResponse', inputContract: 'DevicesFranquias_DisconnectDeviceInput' });
      log('useDevicesFranquias', 'disconnectDevice', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao desconectar dispositivo');
      }
    } catch (err) {
      log('useDevicesFranquias', 'disconnectDevice', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchDevice,
    saveDevice,
    removerDevice,
    setupDevice,
    fetchDeviceToken,
    disconnectDevice,
    toggleActive,
    toggleWebsocket,
    reconnectDevice
  };
}
